import {
  Component,
  EventEmitter,
  Output,
  Input,
  OnInit,
  OnDestroy,
  HostListener,
  SecurityContext,
  ChangeDetectionStrategy
} from '@angular/core';
import { QuillModule } from 'ngx-quill';
import { CommonModule } from '@angular/common';
import Quill, { Delta } from 'quill';
import { FormsModule } from '@angular/forms';
import { GlobalTemplateService } from '../../../../services/global-template.service';
import { Subscription, takeUntil } from 'rxjs';
import { ValidateInputService } from '../../../../services/validate-input.service';
import { Helper } from '../../../../utils/helper';
import { format } from 'crypto-js';
import Block from 'quill/blots/block';
import { VariableMaxLengthService } from '../../../../services/variable-max-length.service';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { ImageUploadComponent } from '../image-upload/image-upload.component';
import { SegmentSelectV2Component } from '../segment-select-v2/segment-select-v2.component';
import { TranslateService } from '../../../../services/translate.service';
import { Subject } from 'rxjs';
import { SegmentDropdownComponent } from '../segment-dropdown/segment-dropdown.component';
import { Segment } from '../../../../models/email-template/segment.interface';
import { segmentsForSelect } from '../../../../utils/segments-helper';
import { TooltipModule } from '@openng/optimus-ui/tooltip';

@Component({
    selector: 'app-block-text',
    imports: [QuillModule, FormsModule, CommonModule, ImageUploadComponent, SegmentDropdownComponent, SegmentSelectV2Component, TooltipModule],
    templateUrl: './block-text.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    styleUrl: './block-text.component.scss'
})
export class BlockTextComponent implements OnInit, OnDestroy {
  @Input() placeholder: string = '';
  @Input() canBeEmpty: boolean = false;
  @Input() blockData: string = '';
  @Input() maxLength: number = 0;
  @Input() type: string = 'black';
  @Input({ required: true }) variable!: string;
  @Input() isImage: boolean = false;
  @Input() blockId: string | undefined;
  @Input() selectedSegment: string = '';
  @Input() isPlainText: boolean = false;
  /** Renders the segment picker as a plain native <select> merged flush
   *  against this field (see field-editor-v2's action-link styling) instead
   *  of the default app-segment-dropdown (PrimeNG p-select) - opt-in so v1's
   *  existing look stays untouched; v2's field-editor-v2 always turns it on. */
  @Input() segmentSelectV2: boolean = false;
  @Input() isSubBlockDeletable: boolean = false;
  /**
   * When set, the text is restricted to Google RSA Search content rules:
   * no gimmicky capitalization (ALL CAPS words, aLtErNaTiNg case,
   * S.P.A.C.E.D. letters), no emojis and no special characters. Abbreviations,
   * coupon codes and intercaps brand names (iPhone, eBay) are allowed.
   * A tooltip describing the rules is shown on the field.
   */
  @Input() restrictContent: boolean = false;

  /** Max length of an all-uppercase word tolerated as an abbreviation (ASAP, HTML, NASA). */
  private static readonly MAX_ALL_CAPS_ABBREVIATION = 4;
  /** Interior capitals (an uppercase letter right after a lowercase one) tolerated per word (iPhone, eBay, McDonald all have 1). */
  private static readonly MAX_INTERIOR_CAPS = 1;
  /** Min single letters in a row (dotted or spaced) that reads as gimmicky (F.L.O.W.E.R.S); short initialisms like U.S.A. stay allowed. */
  private static readonly MAX_SINGLE_LETTER_RUN = 3;
  selectedSegmentFromString: Segment | undefined;

  @Output() handleOnChange = new EventEmitter<string>(); // EventEmitter to send value to parent
  @Output() handleOnSegmentChange = new EventEmitter<string>(); // EventEmitter to send value to parent
  @Output() handleVariableRemoval = new EventEmitter<string>(); // EventEmitter to send variable for removal to parent

  isTranslator: boolean | undefined;
  inputData!: string;
  sanitizedHtml!: SafeHtml;
  quill!: Quill;
  length!: number;
  options: any = {};
  modules: any = {};
  formats: any = null;
  disabled: boolean = true;
  oldHtml: string = '';
  private subscription: Subscription = new Subscription();
  ctrlHeld: boolean = false;
  tooManyChars: boolean = false;
  hasForbiddenContent: boolean = false;
  /** Human-readable list of what `restrictContent` forbids, shown as a tooltip. */
  readonly restrictedContentTooltip =
    'Not allowed:<br/>1) Gimmicky capitalization (FLOWERS, FlOwErS, F.L.O.W.E.R.S)<br/>2) Emojis<br/>3) Special characters<br/>Abbreviations (ASAP), codes and brand names (iPhone) are fine.';
  variableId: string = Helper.makeTimestampedId();
  private _destroy$ = new Subject<void>();
  wasImage: boolean = false;

  constructor(
    private globalTemplateService: GlobalTemplateService,
    private validateInputService: ValidateInputService,
    private _variableMaxLengthService: VariableMaxLengthService,
    private sanitizer: DomSanitizer,
    private translateService: TranslateService
  ) { }

  ngOnInit(): void {
    this.subscription.add(
      this.globalTemplateService
        .getIsDisabled()
        .pipe(takeUntil(this._destroy$))
        .subscribe((isDisabled: boolean) => {
          this.disabled = isDisabled;
        })
    );
    this.inputData = this.blockData;
    this.modules = {
      // Plain-text blocks (e.g. Google RSA headline/description) get no formatting toolbar.
      toolbar: this.isPlainText
        ? false
        : [
            ['bold', 'italic', 'underline', 'strike'],
            ['link'],
            [{ list: 'ordered' }, { list: 'bullet' }],
            [{ script: 'sub' }, { script: 'super' }],
            ['clean'],
          ],
      keyboard: {
        bindings: {
          tab: {
            key: 9,
            handler: function (range: any, context: any) {
              return true;
            },
          },
        },
      },
    };

    this.options = {
      debug: 'info',
      modules: this.modules,
      placeholder: this.placeholder,
      theme: 'snow',
      bounds: 'self',
    };

    // Empty formats list makes Quill strip all formatting, including on paste.
    // null = allow all formats (default rich-text behaviour). Bound on <quill-editor>.
    this.formats = this.isPlainText ? [] : null;

    // Sets up maximum length of a variable in a globally accessible array
    this._variableMaxLengthService.addSingleVariableMaximumLength({
      name: this.variable,
      maxLength: this.maxLength,
    });

    // Gets info if the template is being translated
    this.translateService.isTranslator$
      .pipe(takeUntil(this._destroy$))
      .subscribe((isTranslator) => {
        this.isTranslator = isTranslator;
      });


    this.selectedSegmentFromString = segmentsForSelect.find(s => s.content === this.selectedSegment) ?? undefined;

  }

  /**
   * Emits the image source.
   *
   * @param event - The event data to emit.
   */
  updateInputData(event: any) {
    this.handleOnChange.emit(event);
  }

  ngOnDestroy(): void {
    if (this.subscription) {
      this.subscription.unsubscribe();
    }
    // Delete from validity states on deletion
    this.validateInputService.removeBlockFromStates(this.variableId);

    // Used for unsubscribing
    this._destroy$.next();
    this._destroy$.complete();
  }

  @HostListener('keydown.control', ['$event'])
  onCtrlDown(e: Event) {
    this.ctrlHeld = true;
  }

  @HostListener('keyup.control', ['$event'])
  onCtrlUp(e: Event) {
    this.ctrlHeld = false;
  }

  onContentChanged(event: any) {
    if (!event.html) event.html = '';

    event.html = this.cleanupHTML(event.html);

    event.html = event.html.trim();

    // Replace ALL non‑breaking spaces with plain spaces
    const replacedSpacesTxt = this.ReplaceSpacesType(event);

    this.handleOnChange.emit(replacedSpacesTxt ?? '');
    this.SetOldHtmlBackup(replacedSpacesTxt ?? '');

    // Validate against the RSA content rules on the plain text (no markup).
    this.checkRestrictedContent(event.text ?? '');

    this.CheckHtmlTextLength(replacedSpacesTxt ?? '');
  }

  selectedSegmentChanged(segment: Segment) {
    if (segment == null) {
      this.handleOnSegmentChange.emit("");
      this.selectedSegment = "";
    }
    else {
      this.handleOnSegmentChange.emit(segment.content);
      this.selectedSegment = segment.content;
    }
  }

  removeSubBlock(){
    this.handleVariableRemoval.emit(this.variable);
  }

  private SetOldHtmlBackup(html: string) {
    if (this.quill) {
      this.length = html.length + 1;
    }
    this.oldHtml = html;
  }

  private CheckHtmlTextLength(html: string) {
    this.tooManyChars = html.length > this.maxLength;
    this.updateValidity();
  }

  /**
   * Flags forbidden content when `restrictContent` is on: text that is entirely
   * uppercase, emojis, or special characters. A leading capital letter is allowed.
   */
  private checkRestrictedContent(text: string) {
    this.hasForbiddenContent = this.restrictContent && !this.isContentAllowed(text);
  }

  private isContentAllowed(text: string): boolean {
    const trimmed = text.trim();
    if (trimmed === '') {
      return true;
    }

    // Allow only letters, numbers, whitespace and basic punctuation — anything
    // else (emojis, symbols, other special characters) is forbidden.
    const allowedChars = /^[\p{L}\p{M}\p{N}\s.,'"!?:;()\-–—/&%°*]*$/u;
    if (!allowedChars.test(trimmed)) {
      return false;
    }

    // Forbid gimmicky capitalization (all-caps words, alternating case, spaced
    // out letters) while allowing abbreviations, codes and intercaps brands.
    if (this.hasGimmickyCapitalization(trimmed)) {
      return false;
    }

    return true;
  }

  /**
   * Detects "capitalization that isn't used correctly" per Google Ads rules:
   * ALL-CAPS words (FLOWERS), alternating case (FlOwErS) and letters spaced out
   * with dots or spaces (F.L.O.W.E.R.S / F L O W E R S). Deliberately tolerant of
   * short abbreviations (ASAP, NASA), coupon/model codes (SAVE20, COVID19) and
   * intercaps brand names (iPhone, eBay, McDonald) — Google allows those. Longer
   * all-caps brand names may still be flagged; those can be sent for ad review.
   */
  private hasGimmickyCapitalization(text: string): boolean {
    const isUpper = (ch: string) => ch !== ch.toLowerCase() && ch === ch.toUpperCase();
    const isLower = (ch: string) => ch !== ch.toUpperCase() && ch === ch.toLowerCase();

    // "F.L.O.W.E.R.S" — 4+ single letters each punctuated with a dot.
    if (new RegExp(`(?:\\p{L}\\.){${BlockTextComponent.MAX_SINGLE_LETTER_RUN + 1},}`, 'u').test(text)) {
      return true;
    }

    const words = text.split(/\s+/);

    // "F L O W E R S" — a run of single-letter words. Short initialisms
    // ("U S A") stay under the threshold and are left alone.
    let singleLetterRun = 0;
    for (const word of words) {
      if (/^\p{L}$/u.test(word)) {
        if (++singleLetterRun > BlockTextComponent.MAX_SINGLE_LETTER_RUN) {
          return true;
        }
      } else {
        singleLetterRun = 0;
      }
    }

    for (const word of words) {
      // Compare on letters only; ignore surrounding/embedded punctuation.
      const hasDigit = /\p{N}/u.test(word);
      const letters = [...word].filter(ch => /\p{L}/u.test(ch));
      if (letters.length < 2) {
        continue;
      }

      // "FLOWERS" — all-caps word longer than an abbreviation. Words containing
      // digits are treated as codes/model names (SAVE20, COVID19) and skipped.
      if (
        !hasDigit &&
        letters.every(isUpper) &&
        letters.length > BlockTextComponent.MAX_ALL_CAPS_ABBREVIATION
      ) {
        return true;
      }

      // "FlOwErS" — repeated interior capitals (an uppercase letter right after
      // a lowercase one). Legit intercaps brands have at most one.
      let interiorCaps = 0;
      for (let i = 1; i < letters.length; i++) {
        if (isLower(letters[i - 1]) && isUpper(letters[i])) {
          interiorCaps++;
        }
      }
      if (interiorCaps > BlockTextComponent.MAX_INTERIOR_CAPS) {
        return true;
      }
    }

    return false;
  }

  /** Reports the field as valid only when it passes both length and content rules. */
  private updateValidity() {
    const isValid = !this.tooManyChars && !this.hasForbiddenContent;
    this.validateInputService.reportValidity(this.variableId, isValid);
  }

  /**
   * Replace ALL non‑breaking spaces with plain spaces
   * @param event
   * @returns
   */
  private ReplaceSpacesType(event: any) {
    const withSpaces = event.html.replace(/\u00A0/g, ' ');
    this.sanitizedHtml = this.sanitizer.bypassSecurityTrustHtml(withSpaces);

    // 3) Then re‑sanitize it back into a string
    const stripped = this.sanitizer.sanitize(
      SecurityContext.HTML,
      this.sanitizedHtml
    );
    return stripped;
  }

  onEditorCreated(quill: Quill) {
    this.quill = quill;
    this.quill.root.style.fontFamily = 'Arial, Sans Serif';
    this.quill.root.style.fontSize = '14px';
    this.length = this.inputData.length + 1;
  }

  /**
   * Switches the image input from image upload to text input.
   */
  switchImageToText() {
    this.isImage = !this.isImage;

    // Mark this so that the checkbox can be used again
    this.wasImage = true;
  }

  /**
   * Decode HTML entities like &#39; into their real characters
   * @param {string} html
   * @returns
   */
  private decodeEntities(html: string): string {
    const txt = document.createElement('textarea');
    txt.innerHTML = html;
    return txt.value;
  }

  cleanupHTML(html: string) {
    html = html.replace(/<p[^>]*>/g, '').replace(/<\/p>/g, '<br>'); // Remove only <p> tags
    html = html.replace(/<\/?span\b[^>]*>/gi, ''); // clean up span tags
    html = html.replace(/\s*style=(["'])([\s\S]*?)\1/gi, ''); // then remove any style="…"
    html = html.replace(/(<br\s*\/?>\s*)+$/, ''); // Remove only the last <br> (if it exists)
    html = this.decodeEntities(html);
    if (this.variable.includes('Whatsapp')) {
      html = html.replace(/<br\s*\/?>/gi, '\n');
    }
    return html;
  }
}
